import { useEventListener } from '@vueuse/core';
import { useDialog } from 'naive-ui';
import { onBeforeRouteLeave } from 'vue-router';

/**
 * Stops work being lost to a stray click: while `isDirty()` holds, leaving the
 * page asks first, and closing or reloading the tab gets the browser's own
 * "leave site?" prompt (browsers show their fixed text there, not ours).
 *
 * Call from the setup of any component under a route — vue-router's leave
 * guard works below the route component too, so an editor can guard itself.
 */
export function useUnsavedChangesGuard(isDirty: () => boolean, what = 'Your changes'): void {
  const dialog = useDialog();

  onBeforeRouteLeave(() => {
    if (!isDirty()) return true;
    return new Promise<boolean>(resolve => {
      dialog.warning({
        title: 'Leave without saving?',
        content: `${what} haven’t been saved and will be lost if you leave this page.`,
        positiveText: 'Leave',
        negativeText: 'Keep editing',
        onPositiveClick: () => {
          resolve(true);
        },
        onNegativeClick: () => {
          resolve(false);
        },
        onClose: () => {
          resolve(false);
        },
        onMaskClick: () => {
          resolve(false);
        },
      });
    });
  });

  useEventListener(window, 'beforeunload', (event: BeforeUnloadEvent) => {
    if (isDirty()) event.preventDefault();
  });
}
